import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CircleAlert } from "lucide-react";
import { Button, Field, Page } from "../../components";
import { AuthLayout } from "./AuthLayout";
import { client, unwrap } from "../../api/client";
import { ApiError } from "../../api/errors";
import { t } from "../../i18n/t";
import { MIN_PASSWORD_LENGTH } from "./passwordPolicy";

/** P1a §7: `POST /api/v1/auth/reset-password`, reached from the link a forgot-password email carries. */
export function ResetPasswordScreen(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "invalid">("idle");

  if (token === "" || status === "invalid") {
    // AuthFlows card 06: the centred expired-link card with the circle-alert tile.
    return (
      <AuthLayout>
        <Page narrow title={t("auth.link_expired_title")}>
          <div className="card card--auth card--auth--center">
            <span className="authicon authicon--fail" aria-hidden="true">
              <CircleAlert size={24} strokeWidth={2} />
            </span>
            <p className="authcenter">
              {token === "" ? t("auth.reset.missing_token") : t("auth.reset.invalid")}
            </p>
            <Link className="button button--auth button--primary" to="/auth/forgot-password">
              {t("auth.reset.request_new")}
            </Link>
            <Link className="authcenter" to="/auth/login">
              {t("auth.reset.go_to_login")}
            </Link>
          </div>
        </Page>
      </AuthLayout>
    );
  }

  if (status === "success") {
    return (
      <AuthLayout>
        <Page narrow title={t("auth.reset.title")}>
          <div className="card card--auth card--auth--center">
            <p className="authnote" role="status">
              {t("auth.reset.success")}
            </p>
            <Link className="button button--auth button--primary" to="/auth/login">
              {t("auth.reset.go_to_login")}
            </Link>
          </div>
        </Page>
      </AuthLayout>
    );
  }

  async function onSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (password !== confirm) {
      setError(t("auth.reset.mismatch"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await unwrap(client.POST("/api/v1/auth/reset-password", { body: { token, new_password: password } }));
      setStatus("success");
    } catch (caught) {
      if (caught instanceof ApiError && caught.is("invalid_token")) {
        setStatus("invalid");
      } else if (caught instanceof ApiError && caught.is("rate_limited")) {
        const seconds = caught.details.retry_after_seconds;
        setError(t("auth.login.throttled", { seconds: typeof seconds === "number" ? seconds : 60 }));
      } else {
        setError(t("error.unexpected"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <Page narrow title={t("auth.reset.title")}>
        <form onSubmit={(event) => void onSubmit(event)} className="card card--auth">
          <Field label={t("auth.reset.password")}>
            {({ id, describedBy }) => (
              <input
                id={id}
                aria-describedby={describedBy}
                type="password"
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>
          <Field label={t("auth.reset.confirm")} error={error}>
            {({ id, describedBy }) => (
              <input
                id={id}
                aria-describedby={describedBy}
                type="password"
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
              />
            )}
          </Field>
          <Button
            type="submit"
            variant="primary"
            className="button--auth"
            disabled={busy || password.length < MIN_PASSWORD_LENGTH || confirm === ""}
          >
            {t("auth.reset.submit")}
          </Button>
        </form>
      </Page>
    </AuthLayout>
  );
}
